/**
 * RESOURCES_PROVIDER.JSX - Provider de ressources visuelles
 * 
 * Ce provider fournit un contexte global pour accéder aux ressources
 * visuelles (textures, modèles 3D) dans toute l'application.
 * 
 * Il charge toutes les ressources au montage et les rend accessibles
 * via le hook useResources().
 * 
 * FONCTIONNALITÉS:
 * - Chargement de toutes les ressources
 * - Context API pour partage global
 * - État de chargement (loaded)
 * - Compatible avec Suspense de React
 * 
 * UTILISATION:
 * Wrapper autour de l'application dans App.jsx
 */

import { useState, useEffect, useMemo } from 'react'
import { useGLTF, useTexture } from '@react-three/drei'
import { ResourcesContext } from '../hooks/useResources'
import { RESOURCES_CONFIG } from '../config/resources'

/**
 * ResourcesProvider Component
 * 
 * Charge toutes les ressources et fournit le contexte aux enfants.
 * 
 * @param {object} props
 * @param {React.ReactNode} props.children - Composants enfants
 * @returns {JSX.Element}
 */
export function ResourcesProvider({ children }) {
  const [loaded, setLoaded] = useState(false)
  const [items, setItems] = useState({})

  // ============================================
  // CHARGEMENT DES RESSOURCES
  // ============================================

  useEffect(() => {
    
    // Marquer comme chargé après un court délai
    // Permet au préchargement de se terminer
    const timer = setTimeout(() => {
      setLoaded(true)
    }, 100)

    return () => clearTimeout(timer)
  }, [])

  // ============================================
  // CONSTRUCTION DE L'OBJET ITEMS
  // ============================================
  
  // Note: Dans une implémentation complète, on chargerait ici
  // toutes les ressources via useGLTF/useTexture et on les
  // stockerait dans l'objet items.
  // 
  // Pour l'instant, on laisse les composants charger directement
  // via useModel() et useTextureResource() qui utilisent le cache Drei.
  // 
  // Cette approche est plus simple et tout aussi performante grâce
  // au préchargement et au cache Drei.

  // ============================================
  // VALEUR DU CONTEXTE
  // ============================================

  const contextValue = useMemo(
    () => ({
      items,
      loaded
    }),
    [items, loaded]
  )

  // ============================================
  // RENDER
  // ============================================

  return (
    <ResourcesContext.Provider value={contextValue}>
      {children}
    </ResourcesContext.Provider>
  )
}

/**
 * NOTES D'IMPLÉMENTATION:
 * 
 * 1. APPROCHE SIMPLIFIÉE:
 *    Ce provider est volontairement simple car Drei gère déjà
 *    le cache des ressources de manière optimale.
 *    
 *    Les composants chargent directement via:
 *    - useModel('house') pour les modèles
 *    - useTextureResource('matcapBeige', 'matcap') pour les textures
 *    
 *    Le préchargement (preloadResources()) remplit le cache Drei.
 * 
 * 2. ÉVOLUTION POSSIBLE:
 *    Si besoin d'un objet items centralisé (comme dans vanilla),
 *    charger toutes les ressources ici et les stocker dans state.
 *    
 *    Exemple:
 *    ```javascript
 *    useEffect(() => {
 *      const loadedItems = {}
 *      
 *      // Charger tous les matcaps
 *      RESOURCES_CONFIG.matcaps.forEach(m => {
 *        loadedItems[m.name] = useTexture(m.path)
 *      })
 *      
 *      setItems(loadedItems)
 *    }, [])
 *    ```
 * 
 * 3. PERFORMANCE:
 *    - Context ne re-render que si items/loaded changent
 *    - useMemo évite recréation de l'objet
 *    - Drei cache évite rechargements
 * 
 * 4. ALTERNATIVE:
 *    Utiliser directement les hooks Drei dans composants
 *    sans provider (plus simple, tout aussi efficace).
 *    
 *    Le provider est utile si besoin d'état global centralisé.
 */

